import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Typography, Select, MenuItem, TextField, IconButton, Autocomplete, Box, Button, Divider, Chip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export interface Room {
    id: string;
    name: string;
    width: number;
    height: number;
    maxExpansion: number;
    isFixed: boolean;
}

export interface FixedRoom {
    id: string;
    name: string;
    x: number;
    y: number;
    width: number;
    height: number;
    maxExpansion: number;
}

export interface AdjacencyPair {
    id: string;
    room1: string;
    room2: string;
}

interface LeftNavigationSidebarProps {
    activeSection: "boundary" | "rooms" | "adjacency" | "nonadjacency";
    onSectionChange: (section: "boundary" | "rooms" | "adjacency" | "nonadjacency") => void;
    onGenerateFloorplan: () => void;
    loading: boolean;

    rooms: Room[];
    onRoomsChange: (rooms: Room[]) => void;
    fixedRooms: FixedRoom[];
    onFixedRoomsChange: (rooms: FixedRoom[]) => void;
    onStartFixedRoomDraw: () => void;
    isFixedRoomDrawMode: boolean;
    canUseFixedRoom: boolean;
    adjacencyPairs: AdjacencyPair[];
    onAdjacencyPairsChange: (pairs: AdjacencyPair[]) => void;
    nonAdjacencyPairs: AdjacencyPair[];
    onNonAdjacencyPairsChange: (pairs: AdjacencyPair[]) => void;
    areaStats: {
        totalArea: number;
        roomArea: number;
        remainingArea: number;
        remainingPercentage: number;
        hasBoundary: boolean;
    };
}

const SidebarContainer = styled.div`
    width: 320px; /* Increased slightly to fit inputs */
    height: 100%;
    max-height: 100vh;
    background: #FBE5C6;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    padding: 24px;
    box-sizing: border-box;
    gap: 16px;
    overflow: hidden;
    
    /* Hide scrollbar */
    &::-webkit-scrollbar {
        width: 0px;
    }
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: rgba(26, 26, 26, 0.35) transparent;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 8px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(26, 26, 26, 0.3);
        border-radius: 8px;
        border: 2px solid transparent;
        background-clip: padding-box;
    }

    &:hover::-webkit-scrollbar-thumb {
        background: rgba(26, 26, 26, 0.45);
    }

    &::-webkit-scrollbar-button {
        display: none;
        width: 0;
        height: 0;
    }
`;

const NumberInputField = styled(TextField)`
    width: 62px;
    background: #FBE5C6;

    & .MuiOutlinedInput-root {
        border-radius: 8px;
    }

    & .MuiInputBase-input {
        padding: 8px;
        text-align: center;
        font-size: 13px;
        appearance: textfield;
        -moz-appearance: textfield;
    }

    & .MuiInputBase-input::-webkit-outer-spin-button,
    & .MuiInputBase-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

const LogoSection = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 24px;
    border-bottom: 2px solid #1a1a1a;
    margin-bottom: 8px;

    img {
        width: 142px;
        max-width: 100%;
        height: auto;
        display: block;
    }
`;

const NavSectionHeader = styled.div`
    font-family: Poppins, sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 12px;
`;

const AccordionItem = styled.div<{ $active?: boolean }>`
    display: flex;
    flex-direction: column;
    background: ${(props) => (props.$active ? "transparent" : "transparent")};
    margin-bottom: 8px;
`;

const AccordionHeader = styled.div<{ $active?: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 12px;
    cursor: pointer;
    background: ${(props) => (props.$active ? "#FF9A56" : "transparent")};
    border: 2px solid #1a1a1a;
    color: #1a1a1a;
    font-family: Poppins, sans-serif;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.2s ease;
    
    &:hover {
        background: ${(props) => (props.$active ? "#e8894d" : "rgba(0,0,0,0.05)")};
    }

    img {
        width: 20px;
        height: 20px;
        filter: brightness(0); 
    }
`;

const AccordionContent = styled.div<{ $open: boolean }>`
    display: ${(props) => (props.$open ? "flex" : "none")};
    flex-direction: column;
    padding: 12px 0 12px 0;
    gap: 8px;
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-5px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

const RoomRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px;
    background: rgba(251, 229, 198, 0.5);
    border-radius: 8px;
    border: 1px solid rgba(0,0,0,0.1);
`;

const StyledAddButton = styled.button`
    width: 100%;
    padding: 10px;
    background: rgba(251, 229, 198, 0.6);
    border: 2px dashed #1a1a1a;
    border-radius: 8px;
    color: #1a1a1a;
    font-family: Poppins, sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(251, 229, 198, 0.9);
    }
`;

const GenerateButton = styled.button`
    width: 100%;
    padding: 14px 16px;
    background: #FF9A56;
    border: 2px solid #1a1a1a;
    border-radius: 12px;
    color: #1a1a1a;
    font-family: Poppins, sans-serif;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 0;
    box-shadow: 2px 2px 0px #1a1a1a;

    &:hover {
        transform: translate(-1px, -1px);
        box-shadow: 3px 3px 0px #1a1a1a;
    }
    &:active {
        transform: translate(1px, 1px);
        box-shadow: 1px 1px 0px #1a1a1a;
    }
    &:disabled {
        background: #ccc;
        cursor: not-allowed;
        box-shadow: none;
    }
`;

const AreaInsightsCard = styled.div`
    background: #FBE5C6;
    border: 2px solid #1a1a1a;
    border-radius: 12px;
    padding: 14px;
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
`;

const StatsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const StatRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: Poppins, sans-serif;
    font-size: 13px;
    color: #1f1f1f;
`;

const StatValue = styled.span<{ $highlight?: boolean }>`
    font-weight: ${(props) => (props.$highlight ? 700 : 600)};
    color: ${(props) => (props.$highlight ? "#FF9A56" : "#1a1a1a")};
`;

const ROOM_PRESETS = [
    "Living Room",
    "Dining Room",
    "Kitchen",
    "Bedroom",
    "Bathroom",
    "Balcony",
    "Utility",
    "Study",
    "Guest Room",
    "Corridor",
    "Living",
    "Dining",
];
const ROOM_DEFAULT_DIMENSIONS: Record<string, { width: number; height: number }> = {
    bathroom: { width: 4, height: 6 },
    kitchen: { width: 8, height: 6 },
    "living room": { width: 10, height: 9 },
    living: { width: 10, height: 9 },
    bedroom: { width: 12, height: 10 },
    "dining room": { width: 6, height: 7 },
    dining: { width: 6, height: 7 },
};
const TRAILING_NUMBER_SUFFIX_REGEX = /-\d+$/;
const TRAILING_ROOM_INDEX_REGEX = /(?:\s|-)+\d+$/;

const getRoomNameBase = (name: string) => name.trim().replace(TRAILING_NUMBER_SUFFIX_REGEX, "").trim();
const getRoomTypeBase = (name: string) => name.trim().replace(TRAILING_ROOM_INDEX_REGEX, "").replace(/\s+/g, " ").toLowerCase();

const getRoomDefaultsFromName = (name: string) => ROOM_DEFAULT_DIMENSIONS[getRoomTypeBase(name)];

const enforceUniqueRoomNames = <T extends { name: string }>(items: T[]): T[] => {
    const groupedCounts = new Map<string, number>();
    const forceNumbering = new Map<string, boolean>();

    items.forEach((item) => {
        const trimmed = item.name.trim();
        if (!trimmed) return;

        const baseName = getRoomNameBase(trimmed);
        if (!baseName) return;

        const key = baseName.toLowerCase();
        groupedCounts.set(key, (groupedCounts.get(key) || 0) + 1);

        if (TRAILING_NUMBER_SUFFIX_REGEX.test(trimmed)) {
            forceNumbering.set(key, true);
        }
    });

    const groupedIndices = new Map<string, number>();

    return items.map((item) => {
        const trimmed = item.name.trim();
        if (!trimmed) {
            if (item.name === "") return item;
            return { ...item, name: "" };
        }

        const baseName = getRoomNameBase(trimmed);
        const key = baseName.toLowerCase();
        const duplicateCount = groupedCounts.get(key) || 0;
        const shouldNumber = duplicateCount > 1 || Boolean(forceNumbering.get(key));

        let nextName = trimmed;
        if (shouldNumber) {
            const nextIndex = (groupedIndices.get(key) || 0) + 1;
            groupedIndices.set(key, nextIndex);
            nextName = `${baseName}-${nextIndex}`;
        }

        if (nextName === item.name) return item;
        return { ...item, name: nextName };
    });
};

export const LeftNavigationSidebar: React.FC<LeftNavigationSidebarProps> = ({
    activeSection,
    onSectionChange,
    onGenerateFloorplan,
    loading,
    rooms,
    onRoomsChange,
    fixedRooms,
    onFixedRoomsChange,
    onStartFixedRoomDraw,
    isFixedRoomDrawMode,
    canUseFixedRoom,
    adjacencyPairs,
    onAdjacencyPairsChange,
    nonAdjacencyPairs,
    onNonAdjacencyPairsChange,
    areaStats,
}) => {
    const navigate = useNavigate();

    const addRoom = () => {
        const nextRooms = [
            ...rooms,
            { id: `room-${Date.now()}`, name: "New Room", width: 10, height: 10, maxExpansion: 0, isFixed: false },
        ];
        onRoomsChange(enforceUniqueRoomNames(nextRooms));
    };
    const updateRoom = (id: string, field: keyof Room, value: any) => {
        const nextRooms = rooms.map((room) => {
            if (room.id !== id) return room;

            if (field !== "name") {
                return { ...room, [field]: value };
            }

            const nextName = typeof value === "string" ? value : String(value || "");
            const defaults = getRoomDefaultsFromName(nextName);

            if (!defaults) {
                return { ...room, name: nextName };
            }

            return {
                ...room,
                name: nextName,
                width: defaults.width,
                height: defaults.height,
            };
        });

        if (field === "name") {
            onRoomsChange(enforceUniqueRoomNames(nextRooms));
            return;
        }

        onRoomsChange(nextRooms);
    };
    const deleteRoom = (id: string) => {
        onRoomsChange(enforceUniqueRoomNames(rooms.filter((r) => r.id !== id)));
    };

    const updateFixedRoom = (id: string, field: keyof FixedRoom, value: string | number) => {
        const nextFixedRooms = fixedRooms.map((room) => (room.id === id ? { ...room, [field]: value } : room));

        if (field === "name") {
            onFixedRoomsChange(enforceUniqueRoomNames(nextFixedRooms));
            return;
        }

        onFixedRoomsChange(nextFixedRooms);
    };

    const deleteFixedRoom = (id: string) => {
        onFixedRoomsChange(enforceUniqueRoomNames(fixedRooms.filter((room) => room.id !== id)));
    };

    const deleteAllFixedRooms = () => {
        onFixedRoomsChange([]);
    };

    const deleteAllRooms = () => {
        onRoomsChange([]);
        onAdjacencyPairsChange([]);
        onNonAdjacencyPairsChange([]);
    };

    const addPair = (pairs: AdjacencyPair[], setPairs: (p: AdjacencyPair[]) => void) => {
        const available = rooms.map(r => r.name);
        if (available.length < 2) return;
        setPairs([...pairs, { id: `pair-${Date.now()}`, room1: available[0], room2: available[1] }]);
    };
    const updatePair = (pairs: AdjacencyPair[], setPairs: (p: AdjacencyPair[]) => void, id: string, field: "room1" | "room2", value: string) => {
        setPairs(pairs.map(p => p.id === id ? { ...p, [field]: value } : p));
    };
    const deletePair = (pairs: AdjacencyPair[], setPairs: (p: AdjacencyPair[]) => void, id: string) => {
        setPairs(pairs.filter(p => p.id !== id));
    };

    const availableRoomNames = Array.from(new Set([
        ...rooms.map((r) => r.name),
        ...fixedRooms.map((r) => r.name),
    ].filter((name) => name && name.trim().length > 0)));

    return (
        <SidebarContainer>
            <LogoSection>
                <IconButton
                    onClick={() => navigate("/home")}
                    sx={{ padding: '4px', color: '#1a1a1a' }}
                    title="Back to Home"
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <img src="/gplan logo.svg" alt="logo" />
            </LogoSection>

            <ScrollableContent>
                <NavSectionHeader>Navigation Bar</NavSectionHeader>

                <AccordionItem data-tour="new-input-draw-boundary">
                    <AccordionHeader $active={activeSection === "boundary"} onClick={() => onSectionChange("boundary")}>
                        <img src="/boundary icon.svg" alt="" />
                        <span>Draw Boundary</span>
                    </AccordionHeader>
                </AccordionItem>

                <AccordionItem data-tour="new-input-rooms">
                    <AccordionHeader $active={activeSection === "rooms"} onClick={() => onSectionChange("rooms")}>
                        <img src="/room icon.svg" alt="" />
                        <span style={{ flex: 1 }}>Rooms</span>
                        <ExpandMoreIcon sx={{ transform: activeSection === "rooms" ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }} />
                    </AccordionHeader>
                    <AccordionContent $open={activeSection === "rooms"}>
                        <Typography sx={{ fontFamily: 'Poppins', fontSize: 12, fontWeight: 700, color: '#1a1a1a' }}>
                            Fixed Rooms
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onStartFixedRoomDraw}
                            disabled={!canUseFixedRoom}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                bgcolor: isFixedRoomDrawMode ? '#FF9A56' : '#FF9A56',
                                color: isFixedRoomDrawMode ? '#FBE5C6' : '#1a1a1a',
                                border: '2px solid #1a1a1a',
                                borderRadius: '8px',
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: 'none',
                                    bgcolor: isFixedRoomDrawMode ? '#153a63' : '#e8894d',
                                },
                                '&.Mui-disabled': {
                                    bgcolor: '#e8d8bf',
                                    color: '#8a7f72',
                                    border: '2px solid #b8a88f',
                                },
                            }}
                        >
                            {isFixedRoomDrawMode ? 'Drawing Fixed Room...' : 'Draw Fixed Room'}
                        </Button>
                        {fixedRooms.length > 0 && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DeleteIcon fontSize="small" />}
                                onClick={deleteAllFixedRooms}
                                sx={{
                                    borderColor: "#d66",
                                    color: "#9c2f2f",
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    '&:hover': { borderColor: '#9c2f2f', backgroundColor: 'rgba(156,47,47,0.06)' }
                                }}
                            >
                                Delete All Fixed Rooms
                            </Button>
                        )}
                        {fixedRooms.map((room) => (
                            <RoomRow key={room.id}>
                                <TextField
                                    size="small"
                                    value={room.name}
                                    onChange={(e) => updateFixedRoom(room.id, 'name', e.target.value)}
                                    placeholder="Fixed room name"
                                    sx={{
                                        minWidth: 120,
                                        flex: 1,
                                        "& .MuiOutlinedInput-root": {
                                            height: 36,
                                            background: "#FBE5C6",
                                            fontSize: 13,
                                            borderRadius: "8px",
                                        },
                                    }}
                                />
                                <NumberInputField
                                    size="small"
                                    type="number"
                                    value={room.width}
                                    onChange={(e) => updateFixedRoom(room.id, 'width', Math.max(1, Number(e.target.value || 0)))}
                                    placeholder="W"
                                />
                                <NumberInputField
                                    size="small"
                                    type="number"
                                    value={room.height}
                                    onChange={(e) => updateFixedRoom(room.id, 'height', Math.max(1, Number(e.target.value || 0)))}
                                    placeholder="H"
                                />
                                <IconButton size="small" onClick={() => deleteFixedRoom(room.id)} sx={{ p: 0.5 }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </RoomRow>
                        ))}
                        <Typography sx={{ fontFamily: 'Poppins', fontSize: 11, color: '#666' }}>
                            {canUseFixedRoom
                                ? 'Draw first, then name the fixed room. Fixed rooms are sent in `fixed_rooms`.'
                                : 'Draw a valid closed boundary first to enable fixed room drawing.'}
                        </Typography>
                        <Divider sx={{ my: 1 }} />

                        <Typography sx={{ fontFamily: 'Poppins', fontSize: 12, fontWeight: 700, color: '#1a1a1a' }}>
                            Placeable Rooms
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DeleteIcon fontSize="small" />}
                            onClick={deleteAllRooms}
                            disabled={rooms.length === 0}
                            sx={{
                                borderColor: "#d66",
                                color: "#9c2f2f",
                                borderRadius: "8px",
                                fontWeight: 600,
                                textTransform: "none",
                                mt: 0.5,
                                '&:hover': { borderColor: '#9c2f2f', backgroundColor: 'rgba(156,47,47,0.06)' }
                            }}
                        >
                            Delete All Rooms
                        </Button>
                        {rooms.map(room => (
                            <RoomRow key={room.id}>
                                <Autocomplete
                                    freeSolo
                                    options={ROOM_PRESETS}
                                    value={room.name}
                                    onInputChange={(_, value) => updateRoom(room.id, "name", value)}
                                    onChange={(_, value) => updateRoom(room.id, "name", typeof value === "string" ? value : value || "")}
                                    size="small"
                                    sx={{
                                        minWidth: 120,
                                        flex: 1,
                                        "& .MuiOutlinedInput-root": {
                                            height: 36,
                                            background: "#FBE5C6",
                                            fontSize: 13,
                                            borderRadius: "8px",
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Search/Add room" />
                                    )}
                                />
                                <NumberInputField
                                    size="small"
                                    type="number"
                                    value={room.width}
                                    onChange={(e) => updateRoom(room.id, "width", Math.max(1, Number(e.target.value || 0)))}
                                    placeholder="W"
                                />
                                <NumberInputField
                                    size="small"
                                    type="number"
                                    value={room.height}
                                    onChange={(e) => updateRoom(room.id, "height", Math.max(1, Number(e.target.value || 0)))}
                                    placeholder="H"
                                />
                                <IconButton size="small" onClick={() => deleteRoom(room.id)} sx={{ p: 0.5 }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </RoomRow>
                        ))}
                        <StyledAddButton onClick={addRoom}>
                            <AddIcon fontSize="small" /> Add Room
                        </StyledAddButton>
                    </AccordionContent>
                </AccordionItem>

                {/* Adjacency */}
                <AccordionItem data-tour="new-input-adjacency">
                    <AccordionHeader $active={activeSection === "adjacency"} onClick={() => onSectionChange("adjacency")}>
                        <img src="/adjacency icon.svg" alt="" />
                        <span style={{ flex: 1 }}>Adjacency</span>
                        <ExpandMoreIcon sx={{ transform: activeSection === "adjacency" ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }} />
                    </AccordionHeader>
                    <AccordionContent $open={activeSection === "adjacency"}>
                        {adjacencyPairs.map(pair => (
                            <RoomRow key={pair.id}>
                                <Select
                                    size="small"
                                    value={pair.room1}
                                    onChange={(e) => updatePair(adjacencyPairs, onAdjacencyPairsChange, pair.id, "room1", e.target.value as string)}
                                    sx={{ flex: 1, height: 32, fontSize: 12, bgcolor: 'white' }}
                                >
                                    {availableRoomNames.map(r => <MenuItem key={r} value={r} sx={{ fontSize: 12 }}>{r}</MenuItem>)}
                                </Select>
                                <span style={{ color: '#666', fontSize: 12 }}>↔</span>
                                <Select
                                    size="small"
                                    value={pair.room2}
                                    onChange={(e) => updatePair(adjacencyPairs, onAdjacencyPairsChange, pair.id, "room2", e.target.value as string)}
                                    sx={{ flex: 1, height: 32, fontSize: 12, bgcolor: 'white' }}
                                >
                                    {availableRoomNames.map(r => <MenuItem key={r} value={r} sx={{ fontSize: 12 }}>{r}</MenuItem>)}
                                </Select>
                                <IconButton size="small" onClick={() => deletePair(adjacencyPairs, onAdjacencyPairsChange, pair.id)} sx={{ p: 0.5 }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </RoomRow>
                        ))}
                        {availableRoomNames.length >= 2 ? (
                            <StyledAddButton onClick={() => addPair(adjacencyPairs, onAdjacencyPairsChange)}>
                                <AddIcon fontSize="small" /> Add Adjacency
                            </StyledAddButton>
                        ) : (
                            <Typography variant="caption" sx={{ textAlign: 'center', color: '#666' }}>Add at least 2 rooms</Typography>
                        )}
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem data-tour="new-input-nonadjacency">
                    <AccordionHeader $active={activeSection === "nonadjacency"} onClick={() => onSectionChange("nonadjacency")}>
                        <img src="/non adjacency icon.svg" alt="" />
                        <span style={{ flex: 1 }}>Non-Adjacency</span>
                        <ExpandMoreIcon sx={{ transform: activeSection === "nonadjacency" ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }} />
                    </AccordionHeader>
                    <AccordionContent $open={activeSection === "nonadjacency"}>
                        {nonAdjacencyPairs.map(pair => (
                            <RoomRow key={pair.id}>
                                <Select
                                    size="small"
                                    value={pair.room1}
                                    onChange={(e) => updatePair(nonAdjacencyPairs, onNonAdjacencyPairsChange, pair.id, "room1", e.target.value as string)}
                                    sx={{ flex: 1, height: 32, fontSize: 12, bgcolor: 'white' }}
                                >
                                    {availableRoomNames.map(r => <MenuItem key={r} value={r} sx={{ fontSize: 12 }}>{r}</MenuItem>)}
                                </Select>
                                <span style={{ color: '#666', fontSize: 12 }}>≠</span>
                                <Select
                                    size="small"
                                    value={pair.room2}
                                    onChange={(e) => updatePair(nonAdjacencyPairs, onNonAdjacencyPairsChange, pair.id, "room2", e.target.value as string)}
                                    sx={{ flex: 1, height: 32, fontSize: 12, bgcolor: 'white' }}
                                >
                                    {availableRoomNames.map(r => <MenuItem key={r} value={r} sx={{ fontSize: 12 }}>{r}</MenuItem>)}
                                </Select>
                                <IconButton size="small" onClick={() => deletePair(nonAdjacencyPairs, onNonAdjacencyPairsChange, pair.id)} sx={{ p: 0.5 }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </RoomRow>
                        ))}
                        {availableRoomNames.length >= 2 ? (
                            <StyledAddButton onClick={() => addPair(nonAdjacencyPairs, onNonAdjacencyPairsChange)}>
                                <AddIcon fontSize="small" /> Add Constraint
                            </StyledAddButton>
                        ) : (
                            <Typography variant="caption" sx={{ textAlign: 'center', color: '#666' }}>Add at least 2 rooms</Typography>
                        )}
                    </AccordionContent>
                </AccordionItem>
            </ScrollableContent>

            <GenerateButton data-tour="new-input-generate" onClick={onGenerateFloorplan} disabled={loading}>
                {loading ? "Generating..." : "Generate Floorplan"}
            </GenerateButton>

            <AreaInsightsCard data-tour="new-input-insights">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>
                        Floorplan Insights
                    </Typography>
                    <Chip
                        label={areaStats.hasBoundary ? 'Boundary Ready' : 'Boundary Needed'}
                        size="small"
                        sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            bgcolor: areaStats.hasBoundary ? '#d4f8d9' : '#fbe2cc',
                            color: '#1a1a1a',
                            border: '1px solid #1a1a1a'
                        }}
                    />
                </Box>
                <Divider />
                <StatsGrid>
                    <StatRow>
                        <span>Total Area</span>
                        <StatValue>{areaStats.totalArea.toFixed(2)}</StatValue>
                    </StatRow>
                    <StatRow>
                        <span>Room Area</span>
                        <StatValue>{areaStats.roomArea.toFixed(2)}</StatValue>
                    </StatRow>
                    <StatRow>
                        <span>Remaining Area</span>
                        <StatValue $highlight>{areaStats.remainingArea.toFixed(2)}</StatValue>
                    </StatRow>
                    <StatRow>
                        <span>Remaining %</span>
                        <StatValue $highlight>{areaStats.remainingPercentage.toFixed(1)}%</StatValue>
                    </StatRow>
                </StatsGrid>
                <Typography sx={{ fontFamily: 'Poppins', fontSize: 11, color: '#666', mt: 0.5 }}>
                    Keep remaining area positive for a valid floorplan fit.
                </Typography>
            </AreaInsightsCard>

        </SidebarContainer>
    );
};

export default LeftNavigationSidebar;
